import React, { useCallback, useEffect, useState } from "react";
import { getCdpSessions } from "../../api";
import { fmtLocation } from "../../utils/format";

/* ───────────── helpers ───────────── */
function relativeTime(ts) {
  if (!ts) return "";
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const d = new Date(ts);
  return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function fmtDuration(heartbeats) {
  const secs = (heartbeats || 0) * 30;
  if (secs === 0) return "< 30s";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m === 0) return `${s}s`;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function stripDomain(url) {
  if (!url) return "";
  try { return new URL(url).pathname; } catch { return url; }
}

function truncate(str, max = 35) {
  if (!str || str.length <= max) return str || "";
  return str.slice(0, max) + "\u2026";
}

function maskVisitor(cvId, email) {
  if (email) {
    const [local, domain] = email.split("@");
    return `${local?.[0] || ""}***@${domain || ""}`;
  }
  return cvId ? cvId.slice(0, 8) : "—";
}

const CHANNEL_COLORS = {
  "Paid Search": "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  "Paid Social": "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  "Organic Search": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "Organic Social": "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
  "Direct": "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  "Maps": "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  "Email": "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300",
  "AI": "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",
  "Referral": "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  "Booking Platform": "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300",
  "Display": "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300",
  "Affiliate": "bg-lime-100 text-lime-700 dark:bg-lime-900/40 dark:text-lime-300",
};

const DEVICES = [
  { key: "", label: "All" },
  { key: "mobile", label: "Mobile" },
  { key: "desktop", label: "Desktop" },
  { key: "tablet", label: "Tablet" },
];

const VISITOR_TYPES = [
  { key: "", label: "All" },
  { key: "new", label: "New" },
  { key: "returning", label: "Returning" },
  { key: "known", label: "Known" },
];

/* ═══════════════════ COMPONENT ═══════════════════ */
export default function CdpSessionTable({ clientId, rangeStart, rangeEnd, includeBots = false, onSelectSession, activeSegment }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, total_pages: 0 });

  // Filters
  const [channelFilter, setChannelFilter] = useState("");
  const [deviceFilter, setDeviceFilter] = useState("");
  const [visitorTypeFilter, setVisitorTypeFilter] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [landingSearch, setLandingSearch] = useState("");

  const fetchSessions = useCallback(async (page = 1) => {
    if (!clientId || !rangeStart || !rangeEnd) return;
    try {
      setLoading(true);
      const params = { page, limit: pagination.limit };
      if (channelFilter) params.channel = channelFilter;
      if (deviceFilter) params.device = deviceFilter;
      if (visitorTypeFilter) params.visitor_type = visitorTypeFilter;
      if (locationSearch) params.location = locationSearch;
      if (landingSearch) params.landing_page = landingSearch;
      if (includeBots) params.includeBots = true;

      const resp = await getCdpSessions(clientId, rangeStart, rangeEnd, params);
      const d = resp?.data || {};
      setSessions(d.sessions || []);
      setPagination(p => ({ ...p, page, total: d.pagination?.total || 0, total_pages: d.pagination?.total_pages || 0 }));
    } catch (e) {
      console.error("CDP sessions error:", e);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, rangeStart, rangeEnd, pagination.limit, channelFilter, deviceFilter, visitorTypeFilter, locationSearch, landingSearch, includeBots]);

  // Refetch when deps change
  useEffect(() => { fetchSessions(1); }, [fetchSessions]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPagination(p => ({ ...p, page: 1 }));
  }, [channelFilter, deviceFilter, visitorTypeFilter, locationSearch, landingSearch, activeSegment]);

  const goToPage = (p) => fetchSessions(p);

  return (
    <div className="space-y-4">
      {/* ─── Filters ─── */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Device */}
        <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {DEVICES.map(d => (
            <button
              key={d.key}
              type="button"
              onClick={() => setDeviceFilter(d.key)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                deviceFilter === d.key
                  ? "bg-[#32B1FF] text-white"
                  : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>

        {/* Visitor Type */}
        <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {VISITOR_TYPES.map(v => (
            <button
              key={v.key}
              type="button"
              onClick={() => setVisitorTypeFilter(v.key)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                visitorTypeFilter === v.key
                  ? "bg-[#32B1FF] text-white"
                  : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>

        {/* Channel */}
        <select
          value={channelFilter}
          onChange={e => setChannelFilter(e.target.value)}
          className="px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300"
        >
          <option value="">All Channels</option>
          {Object.keys(CHANNEL_COLORS).map(ch => <option key={ch} value={ch}>{ch}</option>)}
        </select>

        {/* Location */}
        <input
          type="text"
          placeholder="Search location..."
          value={locationSearch}
          onChange={e => setLocationSearch(e.target.value)}
          className="px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 w-36"
        />

        {/* Landing page */}
        <input
          type="text"
          placeholder="Search page..."
          value={landingSearch}
          onChange={e => setLandingSearch(e.target.value)}
          className="px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 w-36"
        />
      </div>

      {/* ─── Table ─── */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="overflow-x-auto responsive-card-table-wrapper">
          <table className="w-full text-sm responsive-card-table">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-[120px]">Time</th>
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-[160px]">Visitor</th>
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-[130px]">Channel</th>
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Landing Page</th>
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-[150px]">Location</th>
                <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-[50px]">Device</th>
                <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-[60px]">Pages</th>
                <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-[90px]">Time on Site</th>
                <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-[120px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-8 text-gray-400">Loading sessions...</td></tr>
              ) : sessions.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-gray-400 text-sm">No sessions found for this period</td></tr>
              ) : sessions.map((s, i) => (
                <tr
                  key={s.session_id || i}
                  onClick={() => onSelectSession?.(s)}
                  className={`border-b border-gray-100 dark:border-gray-800 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                    i % 2 === 1 ? "bg-gray-50/50 dark:bg-gray-900/50" : ""
                  }`}
                >
                  {/* Time */}
                  <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap" data-label="Time">
                    {relativeTime(s.session_start)}
                  </td>

                  {/* Visitor */}
                  <td className="px-3 py-2.5" data-label="Visitor">
                    {s.person_email ? (
                      <span className="text-xs font-medium text-purple-600 dark:text-purple-400">{maskVisitor(s.cv_id, s.person_email)}</span>
                    ) : (
                      <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{maskVisitor(s.cv_id)}</span>
                    )}
                  </td>

                  {/* Channel */}
                  <td className="px-3 py-2.5" data-label="Channel">
                    {s.channel ? (
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${CHANNEL_COLORS[s.channel] || CHANNEL_COLORS["Direct"]}`}>
                        {s.channel}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>

                  {/* Landing Page */}
                  <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 truncate max-w-[250px]" title={s.landing_page || ""} data-label="Landing Page">
                    {truncate(stripDomain(s.landing_page), 35) || "—"}
                  </td>

                  {/* Location */}
                  <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap" data-label="Location">
                    {fmtLocation(s.city, s.region, s.country) || "Unknown"}
                  </td>

                  {/* Device */}
                  <td className="px-3 py-2.5 text-center" data-label="Device">
                    <span title={s.device || "unknown"}>
                      {s.device === "mobile" ? "\ud83d\udcf1" : s.device === "tablet" ? "\ud83d\udcdf" : "\ud83d\udcbb"}
                    </span>
                  </td>

                  {/* Pages */}
                  <td className="px-3 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300" data-label="Pages">{s.page_views ?? 0}</td>

                  {/* Time on Site */}
                  <td className="px-3 py-2.5 text-center text-xs text-gray-600 dark:text-gray-400" data-label="Time on Site">{fmtDuration(s.heartbeats)}</td>

                  {/* Actions */}
                  <td className="px-3 py-2.5 text-center" data-label="Actions">
                    <div className="flex items-center justify-center gap-1">
                      {s.clicked_book && <span title="Book Now Click" className="text-sm">{"\ud83d\uded2"}</span>}
                      {s.completed_booking && <span title="Booking Completed" className="text-sm">{"\u2705"}</span>}
                      {s.clicked_phone && <span title="Phone Click" className="text-sm">{"\ud83d\udcde"}</span>}
                      {s.watched_video && <span title="Video Play" className="text-sm">{"\ud83c\udfac"}</span>}
                      {s.gave_email && <span title="Email Captured" className="text-sm">{"\ud83d\udce7"}</span>}
                      {s.submitted_form && <span title="Form Submit" className="text-sm">{"\ud83d\udcdd"}</span>}
                      {!s.clicked_book && !s.completed_booking && !s.clicked_phone && !s.watched_video && !s.gave_email && !s.submitted_form && (
                        <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ─── Pagination ─── */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-800 max-[399px]:flex-col max-[399px]:items-stretch max-[399px]:gap-2">
            <div className="text-xs text-gray-500 dark:text-gray-400 max-[399px]:text-center">
              Showing {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </div>
            <div className="flex items-center gap-1 max-[399px]:justify-between max-[399px]:w-full">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => goToPage(pagination.page - 1)}
                className="px-2.5 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <span className="px-2 text-xs text-gray-500 dark:text-gray-400">
                {pagination.page} / {pagination.total_pages}
              </span>
              <button
                type="button"
                disabled={pagination.page >= pagination.total_pages}
                onClick={() => goToPage(pagination.page + 1)}
                className="px-2.5 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
